import { useState } from "react";
import { AUTH_URL } from "../lib/api";

const AuthCard = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 w-full max-w-sm">
      <div className="text-center mb-6">
        <img
          src="/logo-white.png"
          alt="Hyve Applied Intelligence"
          className="h-12 mx-auto mb-3"
        />
        <p className="text-sm text-slate-400">Hyve AI Studio</p>
      </div>
      {children}
    </div>
  </div>
);

export const LoginPage = ({ onLogin }) => {
  const [screen, setScreen] = useState("login"); // "login" | "forgot" | "register"

  if (screen === "forgot")
    return <ForgotPasswordScreen onBack={() => setScreen("login")} />;
  if (screen === "register")
    return (
      <RegisterScreen onBack={() => setScreen("login")} onLogin={onLogin} />
    );

  return (
    <LoginScreen
      onLogin={onLogin}
      onForgot={() => setScreen("forgot")}
      onRegister={() => setScreen("register")}
    />
  );
};

const LoginScreen = ({ onLogin, onForgot, onRegister }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem("hyveai_token", data.token);
        onLogin(data.user, data.token);
      } else {
        setError(data.error || "Login failed");
      }
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <AuthCard>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          required
          className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          required
          className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
        />
        {error && <p className="text-red-400 text-sm text-center">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        <button
          onClick={onForgot}
          className="text-slate-400 hover:text-cyan-400 transition-colors"
        >
          Forgot password?
        </button>
        <button
          onClick={onRegister}
          className="text-slate-400 hover:text-cyan-400 transition-colors"
        >
          Create account
        </button>
      </div>
    </AuthCard>
  );
};

const ForgotPasswordScreen = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, app_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
      } else {
        setError(data.error || "Request failed");
      }
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  return (
    <AuthCard>
      {sent ? (
        <div className="text-center space-y-4">
          <p className="text-slate-300 text-sm">
            If that email is registered, you'll receive a password reset link
            shortly.
          </p>
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
          >
            ← Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-white font-semibold text-center">
            Reset Password
          </h2>
          <p className="text-slate-400 text-sm text-center">
            Enter your email and we'll send a reset link.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthCard>
  );
};

const RegisterScreen = ({ onBack, onLogin }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    facility_id: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          facility_id: form.facility_id.trim().toUpperCase(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.error || "Registration failed");
      }
    } catch {
      setError("Cannot connect to server");
    }
    setLoading(false);
  };

  const inputCls =
    "w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none";

  return (
    <AuthCard>
      {success ? (
        <div className="text-center space-y-4">
          <p className="text-slate-300 text-sm">
            Account created! Check your email to confirm your address before
            signing in.
          </p>
          <button
            onClick={onBack}
            className="text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
          >
            ← Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <h2 className="text-white font-semibold text-center mb-1">
            Create Account
          </h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.first_name}
              onChange={set("first_name")}
              placeholder="First name"
              required
              className={inputCls}
            />
            <input
              type="text"
              value={form.last_name}
              onChange={set("last_name")}
              placeholder="Last name"
              required
              className={inputCls}
            />
          </div>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="Email"
            required
            className={inputCls}
          />
          <input
            type="text"
            value={form.facility_id}
            onChange={set("facility_id")}
            placeholder="Facility ID (provided by your admin)"
            required
            className={inputCls}
          />
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            placeholder="Password (8+ characters)"
            required
            className={inputCls}
          />
          <input
            type="password"
            value={form.confirm_password}
            onChange={set("confirm_password")}
            placeholder="Confirm password"
            required
            className={inputCls}
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50"
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              className="text-slate-400 hover:text-cyan-400 text-sm transition-colors"
            >
              ← Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthCard>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD — shows user's facility & services
// ═══════════════════════════════════════════════════════════════════
